/**
 * GeneratorAttributionCard — "Where Your Impact Lands" (Phase 3 + polish).
 *
 * Multi-scale spatial impact map. Top-level layout:
 *
 *   [ Header: title + metric dropdown + congestion badge + Copy button ]
 *   [ SpatialBreakdownBar: "X% global · Y% regional · Z% local" ]
 *   [ Three-endpoint headline — tab-aware ]
 *   [ Tab nav: Local | Regional | Global ]
 *   [ Legend copy + per-tab callouts (Local has a "whose local?" banner) ]
 *   [ Map (Leaflet for Local/Regional, MapLibre globe for Global) ]
 *   [ Inline color/size legend in map corner ]
 *   [ Collapsible plant-detail table ]
 *
 * Scale semantics:
 *   Local    ≈ felt within ~50 km of the source (PM peaks, water depletion)
 *   Regional ≈ felt across ~100-1,500 km airshed (PM + ozone + acidification)
 *   Global   ≈ felt worldwide (climate, ozone depletion, fossil resources)
 *
 * All impacts land AT the displaced plant — not at the project site.
 * The whole UI goes to some lengths to make that clear, because customer
 * review showed it was the single most confusing thing about the Phase 3
 * launch. Hence the "whose local?" banner and the distinct project-site
 * star marker shared across all tabs.
 *
 * Data: `modelResults.summary_json.generator_attribution.generators[]`
 * (spatial_breakdown + impacts_by_category + upstream_attribution).
 * Airshed polygons fetched lazily from /api/v1/projects/{id}/airsheds.
 */

import { useMemo, useState } from "react";
import {
  FiMapPin,
  FiNavigation,
  FiInfo,
  FiCopy,
  FiCheck,
} from "react-icons/fi";
import { MapContainer, TileLayer, useMap, CircleMarker, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import type { DisplacedGeneratorEnriched } from "../../model/domain/types";
import { CongestionBadge } from "./CongestionBadge";
import { SpatialBreakdownBar } from "./SpatialBreakdownBar";
import { LocalImpactLayer } from "./LocalImpactLayer";
import { RegionalAirshedLayer, RAMP_SAMPLE } from "./RegionalAirshedLayer";
import {
  RegionalIntensityLayer,
  IntensityLegend,
} from "./RegionalIntensityLayer";
import { GlobalImpactGlobe, GlobalMetricLegend } from "./GlobalImpactView";
import { BasinAttributionLayer, BasinLegendChip } from "./BasinAttributionLayer";
import { useProjectAirsheds } from "../hooks/useProjectAirsheds";
import { useProjectIntensityTiles } from "../hooks/useProjectIntensityTiles";
import { useProjectAggregate } from "../hooks/useProjectAggregate";
import {
  ChoroplethLayer,
  ChoroplethStats,
  PolygonLookup,
} from "../../portfolio-dashboard/components/PortfolioMap";
import {
  MetricKey,
  fmtGwh,
  fmtMw,
  fmtUsd,
  fmtYearsHealth,
  fmtSpeciesYr,
  getFuelColor,
  getFuelDisplayName,
  totalEndpoints,
  totalEndpointsByScale,
} from "../helpers";
import { CARTO_DARK_URL } from "../../../components/map/basemap";

// ──────────────────────────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────────────────────────

interface GeneratorAttribution {
  total_displaced_mwh: number;
  n_generators: number;
  n_counties: number;
  generators: DisplacedGeneratorEnriched[];
  counties_affected: Record<string, number>;
  states_affected: Record<string, number>;
}

interface CongestionMetadata {
  avg_factor: number;
  constrained_hours: number;
  total_hours: number;
}

interface Props {
  attribution: GeneratorAttribution;
  congestion?: CongestionMetadata | null;
  projectLatitude?: number;
  projectLongitude?: number;
  /** Used for /airsheds cache key — optional so the card still works without it. */
  projectId?: string;
  /** Displayed in the "Copy stakeholder summary" text. */
  projectName?: string;
}

type TabKey = "local" | "regional" | "global";

// ──────────────────────────────────────────────────────────────────────
// Map wrapper — handles fit-to-bounds per tab (Leaflet only)
// ──────────────────────────────────────────────────────────────────────

function MapAutoFit({
  plants,
  projectLatitude,
  projectLongitude,
  tab,
}: {
  plants: DisplacedGeneratorEnriched[];
  projectLatitude?: number;
  projectLongitude?: number;
  tab: TabKey;
}) {
  const map = useMap();

  useMemo(() => {
    const pts: [number, number][] = plants
      .filter((p) => p.latitude && p.longitude)
      .map((p) => [p.latitude, p.longitude]);
    if (projectLatitude && projectLongitude) {
      pts.push([projectLatitude, projectLongitude]);
    }
    if (pts.length === 0) return;
    if (pts.length === 1) {
      map.setView(pts[0], tab === "regional" ? 5 : 7);
      return;
    }
    const bounds = L.latLngBounds(pts.map(([a, b]) => L.latLng(a, b)));
    map.fitBounds(bounds, {
      padding: [30, 30],
      maxZoom: tab === "regional" ? 6 : 9,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, plants.length]);

  return null;
}

// ──────────────────────────────────────────────────────────────────────
// Legend copy per tab
// ──────────────────────────────────────────────────────────────────────

const LEGEND_COPY: Record<TabKey, string> = {
  local:
    "Felt within ~50 km of the source. Most acute near the plant; decays rapidly with distance. Impacts land AT the displaced plant — not at your project.",
  regional:
    "Felt across a ~100-1,500 km airshed downwind. Dominant for air-quality benefits (PM2.5, ozone, acidification).",
  global:
    "Atmospheric mixing — felt worldwide regardless of source location. Dominant for climate, stratospheric ozone, and resource-depletion benefits.",
};

// ──────────────────────────────────────────────────────────────────────
// Three-endpoint headline — tab-aware
// ──────────────────────────────────────────────────────────────────────

function ThreeEndpointHeadline({
  plants,
  tab,
}: {
  plants: DisplacedGeneratorEnriched[];
  tab: TabKey;
}) {
  const totals =
    tab === "local"
      ? totalEndpointsByScale(plants, "local")
      : tab === "regional"
      ? totalEndpointsByScale(plants, "regional")
      : tab === "global"
      ? totalEndpointsByScale(plants, "global")
      : totalEndpoints(plants);

  const intro =
    tab === "local"
      ? "Around displaced plants (~50 km)"
      : tab === "regional"
      ? "Downwind airshed (~100-1,500 km)"
      : "Global (climate + stratospheric ozone + resource depletion)";

  return (
    <div className="mb-3 rounded-lg bg-surface-600/40 border border-border/60 px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">
        {intro}
      </div>
      <p className="text-sm text-text-primary leading-relaxed">
        Your project added{" "}
        <strong
          className="text-quantum-400"
          title="Disability-adjusted life-years: each DALY ≈ one year of healthy life lost or gained. Calculated across every environmental impact pathway — climate, air quality, toxicity, land use, water."
        >
          {fmtYearsHealth(totals.dalys)} of healthy life
        </strong>{" "}
        to populations {tab === "local" ? "near displaced plants" : tab === "regional" ? "downwind" : "worldwide"}, protected{" "}
        <strong
          className="text-quantum-400"
          title="Species·years: ReCiPe ecosystem endpoint. Rough proxy for how much biodiversity loss was avoided."
        >
          {fmtSpeciesYr(totals.pdfs)}
        </strong>{" "}
        of biodiversity, and avoided{" "}
        <strong
          className="text-quantum-400"
          title="USD of avoided resource depletion — fossil, mineral, and natural-resource stocks preserved (ReCiPe resources endpoint, USD 2013)."
        >
          {fmtUsd(totals.usd)}
        </strong>{" "}
        of resource depletion.
      </p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// "Whose local?" callout — Local tab only
// ──────────────────────────────────────────────────────────────────────

function WhoseLocalCallout() {
  return (
    <div className="mb-3 rounded-lg bg-amber-500/10 border border-amber-400/30 px-3 py-2.5 flex items-start gap-2.5">
      <div className="relative flex-shrink-0 mt-0.5">
        <FiMapPin className="text-amber-400" size={16} />
        <div
          className="absolute top-1/2 left-[-2px] w-[18px] h-px bg-amber-300/80"
          style={{ transform: "rotate(25deg)" }}
        />
      </div>
      <FiNavigation className="flex-shrink-0 mt-1 text-amber-300" size={12} />
      <FiMapPin className="flex-shrink-0 mt-0.5 text-amber-200" size={16} />
      <p className="text-xs text-amber-100 leading-snug">
        <strong>Local impacts land around each displaced plant</strong> — not
        around your project site. A solar farm in Austin gets credit for local
        health benefits in Indianapolis if that's where the coal plant it
        displaced is located.
      </p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// In-map legends (floating, top-right)
// ──────────────────────────────────────────────────────────────────────

function LocalLegend({ metric }: { metric: MetricKey }) {
  return (
    <div
      className="absolute top-2 right-2 z-[500] rounded-md bg-surface-700/90 border border-border px-2.5 py-2 text-[10px] text-text-secondary leading-tight backdrop-blur-sm"
      style={{ pointerEvents: "none" }}
    >
      <div className="uppercase tracking-wider text-text-muted mb-1">
        Marker size
      </div>
      <div className="flex items-end gap-2">
        <span
          className="rounded-full bg-text-muted/60"
          style={{ width: 7, height: 7 }}
        />
        <span
          className="rounded-full bg-text-muted/60"
          style={{ width: 14, height: 14 }}
        />
        <span
          className="rounded-full bg-text-muted/60"
          style={{ width: 22, height: 22 }}
        />
      </div>
      <div className="mt-1 text-[10px]">larger = more {metric}</div>
    </div>
  );
}

function RegionalLegend({ metric }: { metric: MetricKey }) {
  return (
    <div
      className="absolute top-2 right-2 z-[500] rounded-md bg-surface-700/90 border border-border px-2.5 py-2 text-[10px] text-text-secondary leading-tight backdrop-blur-sm"
      style={{ pointerEvents: "none" }}
    >
      <div className="uppercase tracking-wider text-text-muted mb-1">
        {metric} per airshed
      </div>
      <div
        className="h-1.5 rounded-full"
        style={{
          width: 120,
          background: `linear-gradient(to right, ${RAMP_SAMPLE.join(", ")})`,
        }}
      />
      <div className="flex justify-between" style={{ width: 120 }}>
        <span>low</span>
        <span>high</span>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Stakeholder-summary clipboard helper
// ──────────────────────────────────────────────────────────────────────

function buildStakeholderSummary(args: {
  projectName?: string;
  totals: { dalys: number; pdfs: number; usd: number };
  states: Array<{ state: string; gwh: number }>;
  displacedGwh: number;
  nGenerators: number;
  projectUrl: string;
}): string {
  const name = args.projectName ?? "TotalView project";
  const topStates = args.states
    .slice(0, 3)
    .map((s) => s.state)
    .filter(Boolean)
    .join(", ");
  return [
    `**Project: ${name}**`,
    "",
    `**Avoided impacts** (full-LCA, ReCiPe endpoints):`,
    `- Health: **${fmtYearsHealth(args.totals.dalys)}** of healthy life added`,
    `- Biodiversity: **${fmtSpeciesYr(args.totals.pdfs)}** protected`,
    `- Resource depletion: **${fmtUsd(args.totals.usd)}** avoided`,
    "",
    `Impacts land around ${args.nGenerators} displaced plants across ${args.states.length} states (largest: ${topStates || "—"}), not at the project site. Total displaced generation: ${fmtGwh(args.displacedGwh)} GWh.`,
    "",
    `Full report: ${args.projectUrl}`,
  ].join("\n");
}

// ──────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────

export function GeneratorAttributionCard({
  attribution,
  congestion,
  projectLatitude,
  projectLongitude,
  projectId,
  projectName,
}: Props) {
  const [tab, setTab] = useState<TabKey>("local");
  const [metric, setMetric] = useState<MetricKey>("DALYs");
  const [hoveredPlantId, setHoveredPlantId] = useState<string | number | null>(null);
  const [showRings, setShowRings] = useState(true);
  const [showBasins, setShowBasins] = useState(true); // on by default (plan §2.4)
  const [copied, setCopied] = useState(false);
  // Phase 4b Regional tab: users can flip between per-plant polygons
  // (default, "by plant") and the true receptor-summed heatmap ("heatmap").
  // We keep polygons as the default for now so analysts can A/B the two
  // views side-by-side; once the heatmap is proven we can flip the
  // default and deprecate the toggle.
  const [regionalMode, setRegionalMode] = useState<
    "polygons" | "heatmap" | "state" | "county" | "cbsa"
  >(
    "polygons",
  );

  const {
    generators,
    n_generators,
    n_counties,
    total_displaced_mwh,
    states_affected,
  } = attribution;

  // Fetch airsheds (tab-aware: only on Regional, only in polygon mode)
  const { data: airsheds } = useProjectAirsheds(
    projectId,
    tab === "regional" && regionalMode === "polygons",
  );

  // Phase 4b: fetch the receptor-sampled intensity tile manifest
  // (only on Regional + heatmap mode; null when tiles aren't built yet).
  const { data: intensityTiles, isError: intensityTilesError } =
    useProjectIntensityTiles(
      projectId,
      tab === "regional" && regionalMode === "heatmap",
    );

  // Per-project boundary aggregate (only when mode ∈ state/county/cbsa)
  const aggregationLevel: "state" | "county" | "cbsa" | null =
    regionalMode === "state" || regionalMode === "county" || regionalMode === "cbsa"
      ? regionalMode
      : null;
  const { data: projectAggregate } = useProjectAggregate(
    projectId, aggregationLevel,
    tab === "regional" && aggregationLevel !== null,
  );

  // If the user flipped to "heatmap" but the project has no tiles built,
  // fall back to polygons silently. Tracks the live response so this
  // responds to React Query refetches too.
  const effectiveRegionalMode:
    | "polygons" | "heatmap" | "state" | "county" | "cbsa" =
    regionalMode === "heatmap" && !intensityTiles ? "polygons" : regionalMode;

  // Sorted plants for the detail table (always by |displaced_gwh|)
  const sortedGenerators = useMemo(
    () =>
      [...generators].sort(
        (a, b) => Math.abs(b.displaced_gwh) - Math.abs(a.displaced_gwh),
      ),
    [generators],
  );

  if (!generators || generators.length === 0) return null;

  const totalDisplacedGwh = total_displaced_mwh / 1000;

  const statePills = Object.entries(states_affected ?? {})
    .sort(([, a], [, b]) => Math.abs(b) - Math.abs(a))
    .map(([state, gwh]) => ({ state, gwh: Math.abs(gwh) }));

  // Basin overlay: off by default on Local tab per plan §2.4
  const basinEnabled = showBasins && tab !== "local" && tab !== "global";

  // Copy stakeholder summary to clipboard (markdown).
  const onCopySummary = async () => {
    const totals = totalEndpoints(generators);
    const summary = buildStakeholderSummary({
      projectName,
      totals,
      states: statePills,
      displacedGwh: totalDisplacedGwh,
      nGenerators: n_generators,
      projectUrl: typeof window !== "undefined" ? window.location.href : "",
    });
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — no-op. The user can still read the card.
    }
  };

  return (
    <div className="mt-4 rounded-lg bg-surface-700 border border-border p-5">
      {/* ─── Header ─────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FiMapPin className="text-quantum-400" size={18} />
            <h3 className="text-body-sm font-semibold text-text-primary">
              Where Your Impact Lands
            </h3>
          </div>
          <p className="text-xs text-text-muted">
            {fmtGwh(totalDisplacedGwh)} GWh displaced across {n_generators} generator
            {n_generators !== 1 ? "s" : ""} in {n_counties} count
            {n_counties !== 1 ? "ies" : "y"}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Metric dropdown */}
          <label className="flex items-center gap-1.5 text-[11px] text-text-muted">
            Metric
            <select
              value={metric}
              onChange={(e) => setMetric(e.target.value as MetricKey)}
              className="bg-surface-600 text-text-primary text-[11px] rounded px-2 py-1 border border-border focus:outline-none focus:border-quantum-500"
            >
              <option value="DALYs">DALYs (health)</option>
              <option value="PDFs">Species-yr (biodiversity)</option>
              <option value="USD">USD (economic)</option>
            </select>
          </label>

          {congestion && (
            <CongestionBadge
              avgFactor={congestion.avg_factor}
              constrainedHours={congestion.constrained_hours}
              totalHours={congestion.total_hours}
            />
          )}

          <button
            onClick={onCopySummary}
            className="inline-flex items-center gap-1.5 text-[11px] rounded border border-border bg-surface-600 hover:bg-surface-500 text-text-secondary hover:text-text-primary px-2.5 py-1 transition-colors"
            title="Copy a short stakeholder-ready summary to the clipboard"
          >
            {copied ? (
              <>
                <FiCheck size={12} className="text-green-400" />
                <span>Copied</span>
              </>
            ) : (
              <>
                <FiCopy size={12} />
                <span>Copy summary</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* ─── State summary pills ─────────────────────────────────── */}
      {statePills.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {statePills.slice(0, 8).map(({ state, gwh }) => (
            <span
              key={state}
              className="inline-flex items-center gap-1 rounded-full bg-surface-600 px-2.5 py-1 text-[11px] font-medium text-text-secondary"
            >
              {state}
              <span className="text-text-primary tabular-nums">{fmtGwh(gwh)} GWh</span>
            </span>
          ))}
        </div>
      )}

      {/* ─── Header % bar ───────────────────────────────────────── */}
      <SpatialBreakdownBar plants={generators} metric={metric} />

      {/* ─── Three-endpoint headline ────────────────────────────── */}
      <ThreeEndpointHeadline plants={generators} tab={tab} />

      {/* ─── Tab nav ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="inline-flex rounded-md bg-surface-600 p-0.5" role="tablist">
          {(["local", "regional", "global"] as TabKey[]).map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={`px-3 py-1 text-[11px] font-medium rounded transition-colors capitalize ${
                tab === t
                  ? "bg-quantum-500/30 text-quantum-400"
                  : "text-text-muted hover:text-text-secondary"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Per-tab toggles */}
        <div className="flex items-center gap-3 text-[11px] text-text-muted">
          {tab === "local" && (
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={showRings}
                onChange={(e) => setShowRings(e.target.checked)}
                className="accent-quantum-500"
              />
              50 km rings
            </label>
          )}
          {tab === "regional" && (
            <>
              {/* Phase 4b — per-plant polygon view vs. receptor-sampled heatmap */}
              <div
                role="radiogroup"
                aria-label="Regional view mode"
                className="inline-flex rounded-md bg-surface-600 p-0.5"
              >
                {(
                  [
                    { k: "polygons", label: "By plant", tip: "Each plant's airshed drawn separately (Phase 3)" },
                    { k: "heatmap", label: "Heatmap", tip: "Receptor-summed PM2.5 intake hotspots (Phase 4b)" },
                    { k: "state", label: "State", tip: "Share of this project's impact by US state" },
                    { k: "county", label: "County", tip: "Share of this project's impact by US county" },
                    { k: "cbsa", label: "Metro", tip: "Share of this project's impact by metropolitan area (CBSA)" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.k}
                    role="radio"
                    aria-checked={regionalMode === opt.k}
                    onClick={() => setRegionalMode(opt.k)}
                    className={`px-2.5 py-0.5 text-[11px] rounded transition-colors ${
                      regionalMode === opt.k
                        ? "bg-quantum-500/30 text-quantum-400"
                        : "text-text-muted hover:text-text-secondary"
                    }`}
                    title={opt.tip}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <label className="inline-flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showBasins}
                  onChange={(e) => setShowBasins(e.target.checked)}
                  className="accent-quantum-500"
                />
                Supplier basins
              </label>
            </>
          )}
          {basinEnabled && <BasinLegendChip />}
        </div>
      </div>

      {/* ─── Legend copy for active tab ──────────────────────────── */}
      <p className="text-[11px] text-text-muted mb-2 leading-snug flex items-start gap-1.5">
        <FiInfo className="flex-shrink-0 mt-0.5" size={10} />
        <span>{LEGEND_COPY[tab]}</span>
      </p>

      {/* ─── "Whose local?" callout ──────────────────────────────── */}
      {tab === "local" && <WhoseLocalCallout />}

      {/* ─── Global tab endpoint-mix legend (above the globe) ──── */}
      {tab === "global" && <GlobalMetricLegend plants={generators} />}

      {/* ─── Map ─────────────────────────────────────────────────── */}
      <div className="relative w-full h-[min(70vh,640px)] min-h-[520px]">
        {tab === "global" ? (
          <GlobalImpactGlobe
            plants={generators}
            projectLatitude={projectLatitude}
            projectLongitude={projectLongitude}
            heightClass="h-[min(70vh,640px)] min-h-[520px]"
          />
        ) : (
          <div className="rounded-lg border border-border overflow-hidden w-full h-full">
            <MapContainer
              center={[39.8, -98.6]}
              zoom={4}
              minZoom={2}
              maxZoom={14}
              zoomSnap={0.5}
              zoomDelta={0.5}
              wheelPxPerZoomLevel={120}
              preferCanvas={true}
              className="h-full w-full"
              style={{ background: "#0A1628", height: "100%" }}
              worldCopyJump
            >
              <TileLayer
                attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                url={CARTO_DARK_URL}
                keepBuffer={2}
                updateWhenZooming={false}
              />

              <MapAutoFit
                plants={generators}
                projectLatitude={projectLatitude}
                projectLongitude={projectLongitude}
                tab={tab}
              />

              {tab === "local" && (
                <LocalImpactLayer
                  plants={generators}
                  metric={metric}
                  projectLatitude={projectLatitude}
                  projectLongitude={projectLongitude}
                  showRings={showRings}
                  onPlantHover={basinEnabled ? setHoveredPlantId : undefined}
                />
              )}

              {tab === "regional" && effectiveRegionalMode === "polygons" && (
                <RegionalAirshedLayer
                  plants={generators}
                  metric={metric}
                  airsheds={airsheds}
                  onPlantHover={basinEnabled ? setHoveredPlantId : undefined}
                />
              )}

              {tab === "regional" &&
                effectiveRegionalMode === "heatmap" &&
                intensityTiles && (
                  <RegionalIntensityLayer
                    tiles={intensityTiles}
                    plants={generators}
                    metric={metric}
                    onPlantHover={basinEnabled ? setHoveredPlantId : undefined}
                  />
                )}

              {tab === "regional" && aggregationLevel && projectAggregate && (
                <>
                  <ChoroplethLayer fc={projectAggregate} level={aggregationLevel} />
                  <PolygonLookup fc={projectAggregate} level={aggregationLevel} />
                  {projectLatitude != null && projectLongitude != null && (
                    <CircleMarker
                      center={[projectLatitude, projectLongitude]}
                      radius={6}
                      pathOptions={{
                        color: "#00e5ff",
                        weight: 2,
                        fillColor: "#ffffff",
                        fillOpacity: 0.95,
                      }}
                    >
                      <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                        <span className="text-[11px]">This project</span>
                      </Tooltip>
                    </CircleMarker>
                  )}
                </>
              )}

              {basinEnabled && (
                <BasinAttributionLayer
                  plants={generators}
                  hoveredPlantId={hoveredPlantId}
                />
              )}
            </MapContainer>
          </div>
        )}

        {/* In-map legends — floating top-right, above the map but below popups */}
        {tab === "local" && <LocalLegend metric={metric} />}
        {tab === "regional" &&
          effectiveRegionalMode === "polygons" && (
            <RegionalLegend metric={metric} />
          )}
        {tab === "regional" && aggregationLevel && projectAggregate && (
          <ChoroplethStats fc={projectAggregate} level={aggregationLevel} />
        )}
        {tab === "regional" &&
          effectiveRegionalMode === "heatmap" &&
          intensityTiles && <IntensityLegend tiles={intensityTiles} />}
      </div>

      {/* ─── Airshed provenance note (Regional tab) ──────────────── */}
      {tab === "regional" && effectiveRegionalMode === "polygons" && (
        <p className="mt-2 text-[10px] text-text-muted leading-snug">
          {airsheds?.features && airsheds.features.length > 0 ? (
            <>
              Polygons: {airsheds.metadata?.source ?? "InMAP ISRM v1.2.1"} ·{" "}
              {airsheds.metadata?.n_plants_with_polygon ?? airsheds.features.length} of{" "}
              {airsheds.metadata?.n_plants_requested ?? n_generators} plants have polygon
              coverage; the rest use a 500 km buffer.
            </>
          ) : (
            <>
              Showing indicative 500 km regional extent per plant. High-fidelity airshed polygons will appear here once the InMAP cache is warmed.
            </>
          )}
        </p>
      )}
      {tab === "regional" && effectiveRegionalMode === "heatmap" && (
        <p className="mt-2 text-[10px] text-text-muted leading-snug">
          {intensityTiles ? (
            <>
              Heatmap: {intensityTiles.citation ?? "InMAP ISRM v1.2.1"} ·
              summed population-weighted PM2.5 intake across{" "}
              {intensityTiles.generator_count ?? n_generators} plants ·{" "}
              {intensityTiles.tile_count ?? "—"} pre-rendered tiles · generated{" "}
              {intensityTiles.generated_at_iso?.slice(0, 10) ?? "—"}.
            </>
          ) : intensityTilesError ? (
            <>
              Intensity tiles unavailable right now — showing per-plant airsheds instead.
            </>
          ) : (
            <>
              Intensity tiles haven&apos;t been built for this project yet —
              showing per-plant airsheds in the meantime. Run{" "}
              <code className="text-text-secondary">scripts/run_intensity_pipeline_ec2.sh</code>{" "}
              to build them.
            </>
          )}
        </p>
      )}

      {/* ─── Plant detail table (collapsible) ────────────────────── */}
      <details className="mt-4 pt-3 border-t border-border/40">
        <summary className="cursor-pointer text-xs text-text-muted hover:text-text-secondary transition-colors select-none">
          Show plant details ({n_generators})
        </summary>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/50">
                <th className="text-left py-2 px-2 text-text-muted font-medium">Plant Name</th>
                <th className="text-left py-2 px-2 text-text-muted font-medium">County, State</th>
                <th className="text-right py-2 px-2 text-text-muted font-medium">Capacity (MW)</th>
                <th className="text-right py-2 px-2 text-text-muted font-medium">Displaced (GWh)</th>
                <th className="text-right py-2 px-2 text-text-muted font-medium">DALYs</th>
                <th className="text-right py-2 px-2 text-text-muted font-medium">USD</th>
              </tr>
            </thead>
            <tbody>
              {sortedGenerators.map((gen) => (
                <tr
                  key={gen.plant_id}
                  className="border-b border-border/30 hover:bg-surface-600/40 transition-colors"
                >
                  <td className="py-2 px-2 text-body-sm">
                    <div className="flex items-center gap-1.5">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: getFuelColor(gen.fuel_type) }}
                      />
                      <span className="text-text-primary">{gen.plant_name}</span>
                      <span className="text-text-muted ml-1">
                        ({getFuelDisplayName(gen.fuel_type)})
                      </span>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-text-secondary text-body-sm">
                    {gen.county}, {gen.state}
                  </td>
                  <td className="py-2 px-2 text-right text-text-secondary tabular-nums text-body-sm">
                    {fmtMw(gen.capacity_mw)}
                  </td>
                  <td className="py-2 px-2 text-right text-text-primary tabular-nums text-body-sm">
                    {fmtGwh(Math.abs(gen.displaced_gwh))}
                  </td>
                  <td className="py-2 px-2 text-right text-text-secondary tabular-nums text-body-sm">
                    {gen.dalys != null ? Math.abs(gen.dalys).toFixed(2) : "—"}
                  </td>
                  <td className="py-2 px-2 text-right text-text-secondary tabular-nums text-body-sm">
                    {gen.usd != null ? fmtUsd(Math.abs(gen.usd)) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
